import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useHistory, useLocation } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Tooltip from "../../ui/kit-mdtw/tooltip/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import InsightsIcon from "@mui/icons-material/Insights";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CrearFormularioModal from "./CrearFormularioModal";
import VerEnlacesModal from "./VerEnlacesModal";
import EditarFormularioModal from "./EditarFormularioModal";
import VerFormularioModal from "./VerFormularioModal";
import MetricasFormularioModal from "./MetricasFormularioModal";
import EstadisticasModal from "./EstadisticasModal";
import ColumnVisibilityMenu from "../../ui/ColumnVisibilityMenu";
import { columnOrderStorageKey, mergeColumnOrder, readColumnOrder, renderOrderedCells } from "../../ui/columnOrder";
import TableSearchInput from "../../ui/TableSearchInput";
import DropDownListCustomized from "../../ui/kit-mdtw/DropDownListCustomized";
import { usePolling } from "../../../hooks/usePolling";
import { PROFILE_ADMIN_SEL } from "../../../diccionary/Diccionary";
import { isTwMarketingProfile } from "../../../config/twMarketingAccess";

// ─── types ────────────────────────────────────────────────────────────────────

type FilterKey =
  | "code"
  | "formType"
  | "formCategory"
  | "client"
  | "business"
  | "vacancy"
  | "recruiter"
  | "status"
  | "createdAt";

interface IFormularioApi {
  contextFormId: string;
  code?: string;
  formType: string;
  client: string;
  businessAreaId: string;
  vacancyId: string;
  recruiterId: string;
  recruiter: string;
  recruitmentSourceId?: string | null;
  recruitmentSource?: string | null;
  formCategoryId?: string | null;
  formCategory?: string | null;
  link: string;
  totalRecords: number;
  totalApplications: number;
  availableApplications: number;
  steps: number;
  createdAt: string;
  status?: string | null;
}

interface IPaginationApi {
  totalRecords: number;
  currentRecords: number;
  currentLimit: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
  startIndex: number;
  endIndex: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
}

interface IFormListResponse {
  pagination: IPaginationApi;
  forms: IFormularioApi[];
}

type ActiveFilters = Record<FilterKey, string[]>;
type FilterInputs  = Record<FilterKey, string>;

// ─── config ───────────────────────────────────────────────────────────────────

const FILTER_KEYS: FilterKey[] = [
  "code", "formType", "formCategory", "client", "business", "vacancy", "recruiter", "status", "createdAt",
];

const FILTER_COLUMNS: {
  key: FilterKey;
  label: string;
  placeholder: string;
  isSelect?: boolean;
}[] = [
  { key: "code",         label: "Código",                 placeholder: "Escribe y presiona Enter" },
  { key: "formType",     label: "Tipo",                   placeholder: "Selecciona…",       isSelect: true },
  { key: "formCategory", label: "Categoría de formulario", placeholder: "Selecciona…",      isSelect: true },
  { key: "status",       label: "Estado",                 placeholder: "Selecciona…",       isSelect: true },
  { key: "client",       label: "Cliente",                placeholder: "Escribe y presiona Enter" },
  { key: "business",     label: "Área de negocio",        placeholder: "Escribe y presiona Enter" },
  { key: "vacancy",      label: "Vacante",                placeholder: "Escribe y presiona Enter" },
  { key: "recruiter",    label: "Reclutador",             placeholder: "Escribe y presiona Enter" },
  { key: "createdAt",    label: "Fecha de creación",      placeholder: "DD-MM-AAAA" },
];

// ─── columnas configurables (visibilidad) ─────────────────────────────────────
// El orden de este arreglo define el orden de columnas y el orden canónico de la
// preferencia guardada. La columna "Acciones" no es configurable (siempre visible).
type TableColumnKey =
  | "code"
  | "formType"
  | "recruitmentSource"
  | "formCategory"
  | "client"
  | "business"
  | "recruiter"
  | "vacancy"
  | "available"
  | "applications"
  | "status"
  | "createdAt";

interface TableColumnDefinition {
  key: TableColumnKey;
  label: string;
  width: number;
}

const TABLE_COLUMN_DEFINITIONS: TableColumnDefinition[] = [
  { key: "code",              label: "Código",                 width: 110 },
  { key: "formType",          label: "Tipo",                   width: 100 },
  { key: "recruitmentSource", label: "Fuente de reclutamiento", width: 160 },
  { key: "formCategory",      label: "Categoría de formulario", width: 170 },
  { key: "client",            label: "Cliente",                width: 130 },
  { key: "business",          label: "Área de negocio",        width: 130 },
  { key: "recruiter",         label: "Reclutador",             width: 150 },
  { key: "vacancy",           label: "Vacante",                width: 100 },
  { key: "available",         label: "Disponibles",            width: 120 },
  { key: "applications",      label: "Postulaciones",          width: 120 },
  { key: "status",            label: "Estado",                 width: 140 },
  { key: "createdAt",         label: "Fecha de creación",      width: 140 },
];

// Orden por defecto de las columnas; el usuario lo cambia arrastrándolas en "Columnas".
const DEFAULT_COLUMN_ORDER: TableColumnKey[] = TABLE_COLUMN_DEFINITIONS.map(({ key }) => key);

const FORMS_TABLE_PREFERENCE_KEY = "FORMULARIOS";
// TW Marketing antes no veía reclutador, vacante ni disponibles, y su preferencia
// guardada enumera solo las columnas que tenía. Ahora tiene acceso completo: se
// le revelan todas una única vez (después puede ocultarlas desde "Columnas").
const TW_MARKETING_FULL_ACCESS_MIGRATION_VERSION = "v1";

const isMigrationPending = (migrationKey: string): boolean => {
  try {
    return localStorage.getItem(migrationKey) !== "complete";
  } catch {
    return true;
  }
};

const normalizeVisibleColumns = (
  value: unknown,
  columnDefinitions: TableColumnDefinition[] = TABLE_COLUMN_DEFINITIONS,
): TableColumnKey[] | null => {
  if (!Array.isArray(value) || value.length === 0) return null;
  const keys = columnDefinitions
    .map(({ key }) => key)
    .filter((key) => value.includes(key));
  return keys.length === value.length ? keys : null;
};

const readVisibleColumns = (
  storageKey: string,
  columnDefinitions: TableColumnDefinition[] = TABLE_COLUMN_DEFINITIONS,
): TableColumnKey[] => {
  try {
    return normalizeVisibleColumns(
      JSON.parse(localStorage.getItem(storageKey) ?? "[]"),
      columnDefinitions,
    ) ?? columnDefinitions.map(({ key }) => key);
  } catch {
    return columnDefinitions.map(({ key }) => key);
  }
};

const FORM_TYPE_OPTIONS = [
  { value: "CLIENTE",    label: "Cliente"    },
  { value: "CUENTA",     label: "Cuenta"     },
  { value: "RECLUTADOR", label: "Reclutador" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVA",           label: "Activo"           },
  { value: "INACTIVO",         label: "Inactivo"         },
  { value: "VACANTE_INACTIVA", label: "Vacante inactiva" },
];

const SELECT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  formType: FORM_TYPE_OPTIONS,
  status:   STATUS_OPTIONS,
};

const TIPO_CONFIG: Record<string, { bg: string; color: string; label: string }> = {
  CLIENTE:    { bg: "#ede9fe", color: "#6d28d9", label: "Cliente"    },
  CUENTA:     { bg: "#dbeafe", color: "#1d4ed8", label: "Cuenta"     },
  RECLUTADOR: { bg: "#fef3c7", color: "#b45309", label: "Reclutador" },
};

type EstadoKey = "ACTIVA" | "INACTIVO" | "VACANTE_INACTIVA";

const ESTADO_CONFIG: Record<EstadoKey, { label: string; bg: string; color: string; dot: string }> = {
  ACTIVA:            { label: "Activo",           bg: "#d1fae5", color: "#047857", dot: "#10b981" },
  INACTIVO:          { label: "Inactivo",         bg: "#fee2e2", color: "#b91c1c", dot: "#ef4444" },
  VACANTE_INACTIVA:  { label: "Vacante inactiva", bg: "#fef3c7", color: "#b45309", dot: "#f59e0b" },
};

const ESTADO_FALLBACK = ESTADO_CONFIG.ACTIVA;

const resolveEstado = (status: IFormularioApi["status"]) => {
  if (status === undefined || status === null) return ESTADO_FALLBACK;
  const key = String(status).trim().toUpperCase() as EstadoKey;
  return ESTADO_CONFIG[key] ?? ESTADO_FALLBACK;
};

const PAGE_SIZES = [10, 20, 50, 100];

const EMPTY_FILTERS: ActiveFilters = {
  code: [], formType: [], formCategory: [], client: [], business: [], vacancy: [], recruiter: [], status: [], createdAt: [],
};
const EMPTY_INPUTS: FilterInputs = {
  code: "", formType: "", formCategory: "", client: "", business: "", vacancy: "", recruiter: "", status: "", createdAt: "",
};

// ─── helpers ──────────────────────────────────────────────────────────────────

// Prefijo de URL para los filtros de esta tabla. Permite que la tabla de
// Postulaciones (que usa el prefijo `p_`) coexista en la misma URL sin que
// sus filtros se pisen al cambiar de tab.
const URL_PREFIX = "f_";
const SEARCH_URL_PARAM = `${URL_PREFIX}q`;

const parseUrl = (search: string) => {
  const sp = new URLSearchParams(search);
  const filters: ActiveFilters = {
    code: [], formType: [], formCategory: [], client: [], business: [], vacancy: [], recruiter: [], status: [], createdAt: [],
  };
  FILTER_KEYS.forEach((k) => {
    const v = sp.get(`${URL_PREFIX}${k}`);
    if (v) filters[k] = v.split(",").map((s) => s.trim()).filter(Boolean);
  });
  const rawPage  = Number(sp.get(`${URL_PREFIX}page`));
  const rawLimit = Number(sp.get(`${URL_PREFIX}limit`));
  const page  = Number.isFinite(rawPage)  && rawPage  > 0 ? rawPage  : 1;
  const limit = PAGE_SIZES.includes(rawLimit) ? rawLimit : 10;
  const searchTerm = (sp.get(SEARCH_URL_PARAM) ?? "").trim();
  return { filters, search: searchTerm, page, limit };
};

const buildSearch = (
  filters: ActiveFilters,
  page: number,
  limit: number,
  current: string,
) => {
  const sp = new URLSearchParams(current);
  FILTER_KEYS.forEach((k) => {
    sp.delete(`${URL_PREFIX}${k}`);
    if (filters[k].length) sp.set(`${URL_PREFIX}${k}`, filters[k].join(","));
  });
  sp.set(`${URL_PREFIX}page`,  String(page));
  sp.set(`${URL_PREFIX}limit`, String(limit));
  return sp.toString();
};

const formatDate = (iso: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd   = String(d.getUTCDate()).padStart(2, "0");
  const mm   = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const filtersEqual = (a: ActiveFilters, b: ActiveFilters) =>
  FILTER_KEYS.every(
    (k) =>
      a[k].length === b[k].length &&
      a[k].every((v, i) => v === b[k][i]),
  );

// ─── pagination ───────────────────────────────────────────────────────────────

interface PaginationProps {
  currentPage:      number;
  totalPages:       number;
  pageSize:         number;
  totalItems:       number;
  onPageChange:     (p: number) => void;
  onPageSizeChange: (s: number) => void;
}

const Pagination = ({
  currentPage, totalPages, pageSize, totalItems,
  onPageChange, onPageSizeChange,
}: PaginationProps) => {
  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end   = Math.min(currentPage * pageSize, totalItems);

  const pages: (number | "…")[] = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const result: (number | "…")[] = [1];
    if (currentPage > 3) result.push("…");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      result.push(i);
    }
    if (currentPage < totalPages - 2) result.push("…");
    if (totalPages > 1) result.push(totalPages);
    return result;
  }, [currentPage, totalPages]);

  const btnBase: React.CSSProperties = {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "34px", height: "34px", borderRadius: "8px",
    border: "1px solid #e2e8f0", background: "#fff",
    fontFamily: "Roboto, sans-serif", fontSize: "13px",
    cursor: "pointer", transition: "all 0.15s", flexShrink: 0,
  };

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", padding: "14px 2px 4px" }}>
      <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#64748b", whiteSpace: "nowrap" }}>
        {totalItems === 0
          ? "Sin resultados"
          : `Mostrando ${start}–${end} de ${totalItems} registro${totalItems !== 1 ? "s" : ""}`}
      </span>

      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          style={{ ...btnBase, color: currentPage <= 1 ? "#cbd5e1" : "#0065DD", cursor: currentPage <= 1 ? "not-allowed" : "pointer" }}
        >
          <KeyboardArrowLeftIcon style={{ fontSize: 18 }} />
        </button>

        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} style={{ width: "34px", textAlign: "center", color: "#94a3b8", fontFamily: "Roboto, sans-serif", fontSize: "13px", userSelect: "none" }}>···</span>
          ) : (
            <motion.button
              key={p}
              onClick={() => onPageChange(p as number)}
              whileTap={{ scale: 0.9 }}
              style={{
                ...btnBase,
                border: p === currentPage ? "none" : "1px solid #e2e8f0",
                background: p === currentPage ? "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)" : "#fff",
                color: p === currentPage ? "#fff" : "#475569",
                fontWeight: p === currentPage ? 600 : 400,
                boxShadow: p === currentPage ? "0 2px 8px rgba(0,101,221,0.28)" : "none",
              }}
            >
              {p}
            </motion.button>
          ),
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || totalPages === 0}
          style={{ ...btnBase, color: currentPage >= totalPages || totalPages === 0 ? "#cbd5e1" : "#0065DD", cursor: currentPage >= totalPages || totalPages === 0 ? "not-allowed" : "pointer" }}
        >
          <KeyboardArrowRightIcon style={{ fontSize: 18 }} />
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#64748b", whiteSpace: "nowrap" }}>Por página</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          style={{
            padding: "6px 28px 6px 10px", border: "1px solid #e2e8f0", borderRadius: "8px",
            fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#334155",
            background: "#fff", cursor: "pointer", outline: "none", appearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center",
          }}
        >
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );
};

// ─── component ────────────────────────────────────────────────────────────────

const TablaFormularios = () => {
  const history  = useHistory();
  const location = useLocation();

  // Sólo los perfiles admin pueden gestionar formularios (crear / editar /
  // activar-desactivar). El reclutador (tw_sel) y TW Marketing ven todo lo
  // demás (postulaciones, enlaces, estadísticas, detalle) pero no gestionan.
  const profile = window.sessionStorage.getItem("profile") ?? "";
  const isTwMarketing = isTwMarketingProfile(profile);
  const canManageForms = PROFILE_ADMIN_SEL.includes(profile);
  const loggedUserId = useMemo(() => sessionStorage.getItem("usuarioId") ?? "", []);
  const columnsStorageKey = useMemo(
    () => `teamwork:formularios:visible-columns:${loggedUserId || "anonymous"}`,
    [loggedUserId],
  );
  const availableColumnDefinitions = TABLE_COLUMN_DEFINITIONS;
  const marketingFullAccessMigrationKey = useMemo(
    () => `teamwork:formularios:tw-marketing-full-access:${TW_MARKETING_FULL_ACCESS_MIGRATION_VERSION}:${loggedUserId || "anonymous"}`,
    [loggedUserId],
  );
  const marketingFullAccessMigrationPendingRef = useRef(
    isTwMarketing && isMigrationPending(marketingFullAccessMigrationKey),
  );
  const applyPendingColumnMigrations = useCallback(
    (keys: TableColumnKey[]): TableColumnKey[] =>
      marketingFullAccessMigrationPendingRef.current
        ? availableColumnDefinitions.map(({ key }) => key)
        : keys,
    [availableColumnDefinitions],
  );

  const { filters: urlFilters, search: urlSearch, page: urlPage, limit: urlLimit } = useMemo(
    () => parseUrl(location.search),
    [location.search],
  );

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<TableColumnKey[]>(
    () => applyPendingColumnMigrations(readVisibleColumns(columnsStorageKey, availableColumnDefinitions)),
  );
  // Orden de TODAS las columnas (visibles u ocultas) elegido por el usuario. Se
  // guarda junto con la visibilidad en su preferencia (columnOrder).
  const [columnOrder, setColumnOrder] = useState<TableColumnKey[]>(
    () => readColumnOrder(columnOrderStorageKey(columnsStorageKey), DEFAULT_COLUMN_ORDER),
  );
  const orderedColumnDefinitions = useMemo(
    () => columnOrder
      .map((key) => availableColumnDefinitions.find((column) => column.key === key))
      .filter((column): column is TableColumnDefinition => Boolean(column)),
    [availableColumnDefinitions, columnOrder],
  );
  const [columnPreferenceLoaded, setColumnPreferenceLoaded] = useState(false);
  const [isSavingColumnPreference, setIsSavingColumnPreference] = useState(false);
  const [isColumnPreferenceSynchronized, setIsColumnPreferenceSynchronized] = useState<boolean | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([]);

  const [filterInputs, setFilterInputs]     = useState<FilterInputs>(EMPTY_INPUTS);
  const [showFilters, setShowFilters]       = useState<boolean>(false);
  const [showCrearModal, setShowCrearModal] = useState<boolean>(false);
  const [enlacesFormulario, setEnlacesFormulario] = useState<IFormularioApi | null>(null);
  const [editarFormulario, setEditarFormulario]   = useState<IFormularioApi | null>(null);
  const [verFormulario, setVerFormulario]         = useState<IFormularioApi | null>(null);
  const [metricasFormulario, setMetricasFormulario] = useState<IFormularioApi | null>(null);
  const [showGeneralStats, setShowGeneralStats]     = useState<boolean>(false);
  const [statsFormulario, setStatsFormulario]       = useState<IFormularioApi | null>(null);

  const [forms, setForms]             = useState<IFormularioApi[]>([]);
  const [pagination, setPagination]   = useState<IPaginationApi | null>(null);
  const [firstLoad, setFirstLoad]     = useState<boolean>(true);
  const firstLoadRef                  = useRef<boolean>(true);
  const [error, setError]             = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState<number>(0);
  // Carga visible (no el polling silencioso): alimenta el "Buscando…" del buscador.
  const [isFetching, setIsFetching]   = useState<boolean>(false);
  const [togglingId, setTogglingId]   = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [filterDropdownVals, setFilterDropdownVals] = useState<Record<string, any>>({
    formType:     { formType: null },
    formCategory: { formCategory: null },
    status:       { status: null },
  });

  // Opciones de select por columna. `formCategory` se alimenta del catálogo.
  const selectOptions = useMemo<Record<string, { value: string; label: string }[]>>(
    () => ({ ...SELECT_OPTIONS, formCategory: categoryOptions }),
    [categoryOptions],
  );

  // Columnas visibles derivadas + ancho mínimo de la tabla.
  const visibleTableColumns = useMemo(
    () => orderedColumnDefinitions.filter(({ key }) => visibleColumnKeys.includes(key)),
    [orderedColumnDefinitions, visibleColumnKeys],
  );
  const tableMinWidth = useMemo(
    () => Math.max(700, (canManageForms ? 220 : 180) + visibleTableColumns.reduce((total, column) => total + column.width, 0)),
    [canManageForms, visibleTableColumns],
  );

  // ── url updater ──────────────────────────────────────────────────────────────
  const updateUrl = useCallback(
    (filters: ActiveFilters, page: number, limit: number) => {
      const newSearch = buildSearch(filters, page, limit, location.search);
      history.replace({ pathname: location.pathname, search: `?${newSearch}` });
    },
    [history, location.pathname, location.search],
  );

  // Buscador global: vive en la URL (como los filtros) y siempre vuelve a la página 1.
  const updateSearch = useCallback(
    (term: string) => {
      const sp = new URLSearchParams(location.search);
      if (term) sp.set(SEARCH_URL_PARAM, term);
      else sp.delete(SEARCH_URL_PARAM);
      sp.set(`${URL_PREFIX}page`, "1");
      history.replace({ pathname: location.pathname, search: `?${sp.toString()}` });
    },
    [history, location.pathname, location.search],
  );

  // Guardas para descartar respuestas obsoletas (cambios de parámetros o un
  // polling en vuelo) y para no llamar a setState tras desmontar.
  const requestIdRef = useRef<number>(0);
  const mountedRef   = useRef<boolean>(true);
  // Petición en vuelo: una nueva la cancela (p. ej. la persona sigue escribiendo
  // en el buscador), así el backend no procesa respuestas que se van a descartar.
  const abortRef     = useRef<AbortController | null>(null);
  // Se re-arma en cada montaje: en StrictMode (dev) el efecto corre
  // mount → unmount → mount, así que hay que volver a poner `true` o las
  // respuestas quedarían descartadas para siempre (skeleton eterno).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // ── carga de la lista (la usan el efecto de parámetros y el polling) ──────────
  // Con `silent: true` es un refresco en segundo plano: no muestra skeleton ni
  // banner de error y conserva los datos actuales si la vuelta falla, para que
  // el usuario no note la actualización.
  const fetchForms = useCallback(
    (options?: { silent?: boolean }) => {
      const silent  = options?.silent ?? false;
      const userId  = sessionStorage.getItem("usuarioId") ?? "";
      const baseUrl = process.env.REACT_APP_API_APPLICANT;
      if (!baseUrl) {
        setError("Configuración faltante: REACT_APP_API_APPLICANT");
        setForms([]);
        setPagination(null);
        firstLoadRef.current = false;
        setFirstLoad(false);
        return;
      }

      const isInitialThisRun = firstLoadRef.current;
      const requestId = ++requestIdRef.current;

      const filtersBody = FILTER_KEYS.reduce<Record<string, string[] | string>>((acc, k) => {
        if (urlFilters[k].length > 0) acc[k] = urlFilters[k];
        return acc;
      }, {});
      if (urlSearch) filtersBody.search = urlSearch;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (!silent) setIsFetching(true);

      const url =
        `${baseUrl}/form/list` +
        `?page=${urlPage}` +
        `&limit=${urlLimit}` +
        `&userId=${encodeURIComponent(userId)}`;

      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filters: filtersBody }),
        signal: controller.signal,
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return (await res.json()) as IFormListResponse;
        })
        .then((data) => {
          // Descarta si ya hay una petición más nueva o si se desmontó.
          if (!mountedRef.current || requestId !== requestIdRef.current) return;
          setForms(Array.isArray(data?.forms) ? data.forms : []);
          setPagination(data?.pagination ?? null);
          setError(null);
        })
        .catch(() => {
          if (!mountedRef.current || requestId !== requestIdRef.current) return;
          // Un polling fallido conserva lo que ya está en pantalla y reintenta
          // en la próxima vuelta, sin parpadeos ni error visible.
          if (silent) return;
          if (isInitialThisRun) {
            setForms([]);
            setPagination(null);
          }
          setError("No se pudo cargar la lista de formularios.");
        })
        .finally(() => {
          if (!mountedRef.current || requestId !== requestIdRef.current) return;
          setIsFetching(false);
          if (isInitialThisRun) {
            firstLoadRef.current = false;
            setFirstLoad(false);
          }
        });
    },
    [urlFilters, urlSearch, urlPage, urlLimit],
  );

  // Carga inicial y recargas por cambio de parámetros o acciones del usuario
  // (crear / editar / activar). No usa `silent`: aquí sí puede animarse la tabla.
  useEffect(() => {
    fetchForms();
  }, [fetchForms, refreshTick]);

  // ── catálogo de categorías de formulario (para el filtro) ─────────────────────
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;

    let cancelled = false;
    fetch(`${baseUrl}/catalogs/form-categories`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.formCategories) ? data.formCategories : [];
        const names = rows
          .map((row: any) => (typeof row?.name === "string" ? row.name.trim() : ""))
          .filter(Boolean);
        setCategoryOptions(Array.from(new Set<string>(names)).map((name) => ({ value: name, label: name })));
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([]);
      });

    return () => { cancelled = true; };
  }, []);

  // ── preferencia de columnas: carga inicial desde la cuenta ────────────────────
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    let cancelled = false;

    if (!baseUrl || !loggedUserId) {
      setColumnPreferenceLoaded(true);
      return;
    }

    const params = new URLSearchParams({
      userId: loggedUserId,
      table: FORMS_TABLE_PREFERENCE_KEY,
    });

    fetch(`${baseUrl}/application/table-preferences?${params.toString()}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setIsColumnPreferenceSynchronized(true);
        const normalizedRemoteColumns = normalizeVisibleColumns(
          data?.preference?.visibleColumns,
          availableColumnDefinitions,
        );
        const remoteColumns = normalizedRemoteColumns
          ? applyPendingColumnMigrations(normalizedRemoteColumns)
          : normalizedRemoteColumns;
        if (remoteColumns) {
          setVisibleColumnKeys(remoteColumns);
          try {
            localStorage.setItem(columnsStorageKey, JSON.stringify(remoteColumns));
          } catch {
            // La preferencia remota sigue disponible aunque localStorage esté bloqueado.
          }
        }
        // Sin columnOrder (preferencia guardada antes del drag and drop) se
        // mantiene el orden local.
        if (Array.isArray(data?.preference?.columnOrder)) {
          const remoteOrder = mergeColumnOrder(data.preference.columnOrder, DEFAULT_COLUMN_ORDER);
          setColumnOrder(remoteOrder);
          try {
            localStorage.setItem(columnOrderStorageKey(columnsStorageKey), JSON.stringify(remoteOrder));
          } catch {
            // Igual que arriba: el orden remoto se aplica aunque no se pueda copiar localmente.
          }
        }
      })
      .catch(() => {
        if (!cancelled) setIsColumnPreferenceSynchronized(false);
      })
      .finally(() => {
        if (!cancelled) setColumnPreferenceLoaded(true);
      });

    return () => { cancelled = true; };
  }, [applyPendingColumnMigrations, availableColumnDefinitions, columnsStorageKey, loggedUserId]);

  // ── preferencia de columnas: persistencia local + sincronización con la cuenta ─
  useEffect(() => {
    try {
      localStorage.setItem(columnsStorageKey, JSON.stringify(visibleColumnKeys));
      localStorage.setItem(columnOrderStorageKey(columnsStorageKey), JSON.stringify(columnOrder));
    } catch {
      // La tabla sigue funcionando aunque el navegador bloquee localStorage.
    }
    if (!columnPreferenceLoaded || !loggedUserId) return;

    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;

    let controller: AbortController | null = null;
    const saveTimer = window.setTimeout(() => {
      controller = new AbortController();
      setIsSavingColumnPreference(true);
      const params = new URLSearchParams({
        userId: loggedUserId,
        table: FORMS_TABLE_PREFERENCE_KEY,
      });
      fetch(`${baseUrl}/application/table-preferences?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // visibleColumns va en el orden del usuario; columnOrder incluye las ocultas.
        body: JSON.stringify({
          visibleColumns: columnOrder.filter((key) => visibleColumnKeys.includes(key)),
          columnOrder,
        }),
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          setIsColumnPreferenceSynchronized(true);
          if (marketingFullAccessMigrationPendingRef.current) {
            try {
              localStorage.setItem(marketingFullAccessMigrationKey, "complete");
              marketingFullAccessMigrationPendingRef.current = false;
            } catch {
              // Las columnas seguirán visibles aunque el navegador bloquee localStorage.
            }
          }
        })
        .catch((requestError) => {
          if (requestError?.name !== "AbortError") {
            setIsColumnPreferenceSynchronized(false);
          }
        })
        .finally(() => {
          if (!controller?.signal.aborted) setIsSavingColumnPreference(false);
        });
    }, 350);

    return () => {
      window.clearTimeout(saveTimer);
      controller?.abort();
    };
  }, [columnOrder, columnPreferenceLoaded, columnsStorageKey, loggedUserId, marketingFullAccessMigrationKey, visibleColumnKeys]);

  // ── filter actions ───────────────────────────────────────────────────────────
  const addFilter = (key: FilterKey, value: string) => {
    const v = value.trim();
    if (!v) return;
    if (urlFilters[key].includes(v)) {
      setFilterInputs((prev) => ({ ...prev, [key]: "" }));
      return;
    }
    const next: ActiveFilters = { ...urlFilters, [key]: [...urlFilters[key], v] };
    setFilterInputs((prev) => ({ ...prev, [key]: "" }));
    updateUrl(next, 1, urlLimit);
  };

  const handleSelectFilterChange = (key: FilterKey, vals: any) => {
    const code: string | undefined = vals?.[key]?.value?.code;
    if (!code) return;
    addFilter(key, code);
    setFilterDropdownVals((prev) => ({ ...prev, [key]: { [key]: null } }));
  };

  const removeFilter = (key: FilterKey, value: string) => {
    const next: ActiveFilters = { ...urlFilters, [key]: urlFilters[key].filter((v) => v !== value) };
    updateUrl(next, 1, urlLimit);
  };

  const clearAllFilters = () => {
    if (filtersEqual(urlFilters, EMPTY_FILTERS)) return;
    setFilterInputs(EMPTY_INPUTS);
    updateUrl(EMPTY_FILTERS, 1, urlLimit);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, key: FilterKey) => {
    if (e.key === "Enter") addFilter(key, filterInputs[key]);
  };

  const handlePageChange     = (p: number) => updateUrl(urlFilters, p, urlLimit);
  const handlePageSizeChange = (s: number) => updateUrl(urlFilters, 1, s);

  const totalActiveFilters = useMemo(
    () => FILTER_KEYS.reduce((acc, k) => acc + urlFilters[k].length, 0),
    [urlFilters],
  );

  // ── derived ─────────────────────────────────────────────────────────────────
  const currentPage = pagination?.currentPage ?? urlPage;
  const totalPages  = pagination?.totalPages  ?? 1;
  const totalItems  = pagination?.totalRecords ?? forms.length;
  const pageSize    = pagination?.currentLimit ?? urlLimit;

  const handleVer = (f: IFormularioApi) => {
    if (!f.contextFormId) return;
    setVerFormulario(f);
  };

  useEffect(() => {
    if (!toggleError) return;
    const t = setTimeout(() => setToggleError(null), 4000);
    return () => clearTimeout(t);
  }, [toggleError]);

  const handleToggleStatus = async (f: IFormularioApi) => {
    if (!f.contextFormId || togglingId) return;
    const currentEstado = resolveEstado(f.status);
    if (currentEstado === ESTADO_CONFIG.VACANTE_INACTIVA) return;

    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) {
      setToggleError("Configuración faltante: REACT_APP_API_APPLICANT");
      return;
    }

    const userId       = sessionStorage.getItem("usuarioId") ?? "";
    const wasActive    = currentEstado === ESTADO_CONFIG.ACTIVA;
    const nextActive   = !wasActive;
    const optimisticSt = nextActive ? "ACTIVA" : "INACTIVO";
    const previousSt   = f.status ?? null;

    setTogglingId(f.contextFormId);
    setToggleError(null);
    setForms((prev) =>
      prev.map((row) =>
        row.contextFormId === f.contextFormId ? { ...row, status: optimisticSt } : row,
      ),
    );

    try {
      const url =
        `${baseUrl}/form/status/${encodeURIComponent(f.contextFormId)}` +
        `?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: nextActive }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      setForms((prev) =>
        prev.map((row) =>
          row.contextFormId === f.contextFormId ? { ...row, status: previousSt } : row,
        ),
      );
      setToggleError("No se pudo actualizar el estado del formulario.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleEditar = (f: IFormularioApi) => {
    if (!f.contextFormId) return;
    setEditarFormulario(f);
  };

  const handleVerPostulaciones = (f: IFormularioApi) => {
    if (!f.code) return;
    history.push(`/postulaciones?tab=postulaciones&p_formCode=${encodeURIComponent(f.code)}`);
  };

  const handleVerDisponibles = (f: IFormularioApi) => {
    if (!f.code) return;
    history.push(
      `/postulaciones?tab=postulaciones&p_formCode=${encodeURIComponent(f.code)}&p_status=DISPONIBLE`,
    );
  };

  // ── polling silencioso de la tabla ────────────────────────────────────────────
  // Refresca la lista en segundo plano para dar sensación de tiempo real. Se
  // PAUSA mientras hay un modal de estadísticas abierto (general o por
  // formulario): en ese caso solo el modal consulta al servidor, para no
  // duplicar la carga. Al cerrarlo se retoma el timer.
  const isStatsModalOpen = showGeneralStats || statsFormulario !== null;
  usePolling(
    () => fetchForms({ silent: true }),
    { enabled: !firstLoad && !isStatsModalOpen },
  );

  // Al cerrar el modal de estadísticas, sincroniza la tabla de inmediato en
  // lugar de esperar al siguiente tick (pudo quedar desfasada mientras estuvo
  // abierto).
  const prevStatsModalOpenRef = useRef<boolean>(isStatsModalOpen);
  useEffect(() => {
    if (prevStatsModalOpenRef.current && !isStatsModalOpen && !firstLoad) {
      fetchForms({ silent: true });
    }
    prevStatsModalOpenRef.current = isStatsModalOpen;
  }, [isStatsModalOpen, firstLoad, fetchForms]);

  return (
    <>
      <style>{`
        .form-row { transition: background 0.12s ease; }
        .form-row:hover { background: rgba(0,101,221,0.038) !important; }
        .form-row:hover td:first-child { border-left-color: #0065DD !important; }
        @keyframes form-header-breathe {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.82; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .form-th-accent { animation: form-header-breathe 4s ease-in-out infinite; }
        .form-status-dot {
          display: inline-block; width: 7px; height: 7px;
          border-radius: 50%; margin-right: 5px; flex-shrink: 0;
          vertical-align: middle;
        }
        .form-filter-input {
          width: 100%;
          padding: 7px 10px;
          font-size: 12px;
          font-family: Roboto, sans-serif;
          border: 1px solid #e2e8f0;
          border-radius: 7px;
          outline: none;
          color: #334155;
          background: #fff;
          transition: border-color 0.15s, box-shadow 0.15s;
          box-sizing: border-box;
        }
        .form-filter-input:focus {
          border-color: #0065DD;
          box-shadow: 0 0 0 3px rgba(0,101,221,0.1);
        }
      `}</style>

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "12px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "1 1 auto", flexWrap: "wrap" }}>
          <motion.button
            onClick={() => setShowFilters((p) => !p)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            style={{
              display: "flex", alignItems: "center", gap: "7px",
              padding: "7px 16px", borderRadius: "8px",
              border: showFilters ? "1.5px solid #0065DD" : "1px solid #e2e8f0",
              background: showFilters ? "#eff6ff" : "#fff",
              color: showFilters ? "#0065DD" : "#475569",
              fontFamily: "Roboto, sans-serif", fontSize: "13px",
              fontWeight: 500, cursor: "pointer", position: "relative",
            }}
          >
            <TuneIcon style={{ fontSize: 16 }} />
            Filtros
            <AnimatePresence>
              {totalActiveFilters > 0 && (
                <motion.span
                  initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                  style={{ position: "absolute", top: "-7px", right: "-7px", width: "18px", height: "18px", borderRadius: "50%", background: "#0065DD", color: "#fff", fontSize: "10px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  {totalActiveFilters}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          <ColumnVisibilityMenu
            columns={orderedColumnDefinitions}
            visibleKeys={visibleColumnKeys}
            onChange={(keys) => setVisibleColumnKeys(keys as TableColumnKey[])}
            onReorder={(keys) => setColumnOrder(mergeColumnOrder(keys, DEFAULT_COLUMN_ORDER))}
            defaultOrder={DEFAULT_COLUMN_ORDER}
            isSaving={isSavingColumnPreference}
            isSynchronized={isColumnPreferenceSynchronized}
          />

          <TableSearchInput
            value={urlSearch}
            onSearch={updateSearch}
            loading={isFetching}
            placeholder="Buscar formularios…"
          />

          <AnimatePresence>
            {totalActiveFilters > 0 && (
              <motion.button
                initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                onClick={clearAllFilters}
                style={{ display: "flex", alignItems: "center", gap: "4px", padding: "5px 12px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fff5f5", color: "#b91c1c", fontFamily: "Roboto, sans-serif", fontSize: "12px", cursor: "pointer" }}
              >
                <CloseIcon style={{ fontSize: 13 }} />
                Limpiar filtros
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* El backend aplica el alcance del perfil a las estadísticas generales. */}
          <Tooltip text="Estadísticas">
            <motion.button
              onClick={() => setShowGeneralStats(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              aria-label="Estadísticas"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "38px", height: "38px", borderRadius: "8px",
                border: "1px solid #e2e8f0", background: "#fff",
                color: "#0065DD", cursor: "pointer",
              }}
            >
              <QueryStatsIcon style={{ fontSize: 20 }} />
            </motion.button>
          </Tooltip>

          {canManageForms && (
            <>
              <style>{`
                .crear-form-btn { position: relative; overflow: hidden; isolation: isolate; }
                .crear-form-btn::after {
                  content: "";
                  position: absolute;
                  top: 0; left: 0;
                  width: 40%; height: 100%;
                  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%);
                  transform: translateX(-120%) skewX(-20deg);
                  transition: transform 0.4s ease-in-out;
                  pointer-events: none;
                  z-index: 1;
                }
                .crear-form-btn:hover::after {
                  transform: translateX(260%) skewX(-20deg);
                }
                .crear-form-btn > * { position: relative; z-index: 2; }
              `}</style>
              <motion.button
                className="crear-form-btn"
                onClick={() => setShowCrearModal(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  display: "flex", alignItems: "center", gap: "7px",
                  padding: "8px 18px", borderRadius: "8px", border: "none",
                  background: "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)",
                  color: "#fff", fontFamily: "Roboto, sans-serif", fontSize: "13px",
                  fontWeight: 600, cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,101,221,0.28)",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Crear formulario
              </motion.button>
            </>
          )}
        </div>
      </div>

      {/* ── Filter panel ────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            style={{ marginBottom: "16px", position: "relative", zIndex: 5 }}
          >
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px 24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
                {FILTER_COLUMNS.map((col) => (
                  <div key={col.key}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                      <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                        {col.label}
                      </span>
                      <AnimatePresence>
                        {urlFilters[col.key].length > 0 && (
                          <motion.span
                            initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                            style={{ background: "#0065DD", color: "#fff", borderRadius: "8px", padding: "0 6px", fontSize: "10px", fontWeight: 700, lineHeight: "16px" }}
                          >
                            {urlFilters[col.key].length}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>

                    {col.isSelect ? (
                      <DropDownListCustomized
                        key={`filter-${col.key}-${urlFilters[col.key].length}`}
                        list={(selectOptions[col.key] ?? [])
                          .filter((opt) => !urlFilters[col.key].includes(opt.value))
                          .map((opt) => ({ name: opt.label, code: opt.value }))}
                        layer=""
                        required={false}
                        errorPosition="down"
                        onValueChange={(vals: any) => handleSelectFilterChange(col.key, vals)}
                        onPermaValues={filterDropdownVals[col.key]}
                        onPropertyChange={col.key}
                        placeholder={col.placeholder}
                      />
                    ) : (
                      <input
                        type="text"
                        className="form-filter-input"
                        style={{ width: "100%" }}
                        placeholder={col.placeholder}
                        value={filterInputs[col.key]}
                        inputMode={col.key === "vacancy" || col.key === "createdAt" ? "numeric" : "text"}
                        onChange={(e) => {
                          let v = e.target.value;
                          if (col.key === "vacancy")   v = v.replace(/\D/g, "");
                          if (col.key === "createdAt") v = v.replace(/[^0-9-]/g, "");
                          setFilterInputs((p) => ({ ...p, [col.key]: v }));
                        }}
                        onKeyDown={(e) => handleKeyDown(e, col.key)}
                      />
                    )}

                    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "7px", minHeight: "4px" }}>
                      <AnimatePresence>
                        {urlFilters[col.key].map((val) => {
                          const tipoCfg   = col.key === "formType" ? TIPO_CONFIG[val] : null;
                          const estadoCfg = col.key === "status"
                            ? ESTADO_CONFIG[val as EstadoKey]
                            : null;
                          const cfg   = tipoCfg ?? estadoCfg;
                          const label = cfg ? cfg.label : val;
                          return (
                            <motion.span
                              key={val}
                              initial={{ opacity: 0, scale: 0.75 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.75 }}
                              transition={{ type: "spring", stiffness: 400, damping: 20 }}
                              style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px 3px 10px", borderRadius: "14px", background: cfg ? cfg.bg : "#e0f2fe", border: `1px solid ${cfg ? cfg.color + "33" : "#bae6fd"}`, color: cfg ? cfg.color : "#0369a1", fontSize: "11px", fontFamily: "Roboto, sans-serif", fontWeight: 500, whiteSpace: "nowrap" }}
                            >
                              {label}
                              <button
                                onClick={() => removeFilter(col.key, val)}
                                style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0, color: "inherit", opacity: 0.65, lineHeight: 1 }}
                              >
                                <CloseIcon style={{ fontSize: 11 }} />
                              </button>
                            </motion.span>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toggleError && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            style={{
              marginBottom: "10px",
              padding: "8px 14px",
              borderRadius: "8px",
              background: "#fff5f5",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              fontFamily: "Roboto, sans-serif",
              fontSize: "12.5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <span>{toggleError}</span>
            <button
              onClick={() => setToggleError(null)}
              style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", display: "flex", padding: 0 }}
            >
              <CloseIcon style={{ fontSize: 14 }} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <div style={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 8px rgba(15,23,42,0.07), 0 0 0 1px rgba(15,23,42,0.06)", background: "#fff" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: `${tableMinWidth}px`, tableLayout: "fixed" }}>
            <colgroup>
              {visibleTableColumns.map((column) => <col key={column.key} style={{ width: `${column.width}px` }} />)}
              <col style={{ width: canManageForms ? "220px" : "180px" }} />
            </colgroup>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}>
                {visibleTableColumns.map((column, i) => (
                  <th
                    key={column.key}
                    className={i === 0 ? "form-th-accent" : ""}
                    style={{ padding: "13px 16px", fontSize: "10.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.09em", textAlign: column.key === "applications" || column.key === "available" ? "center" : "left", whiteSpace: "nowrap", borderBottom: "2px solid rgba(56,189,248,0.22)" }}
                  >
                    {column.label}
                  </th>
                ))}
                <th
                  style={{ padding: "13px 16px", fontSize: "10.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.09em", textAlign: "left", whiteSpace: "nowrap", borderBottom: "2px solid rgba(56,189,248,0.22)" }}
                >
                  Acciones
                </th>
              </tr>
            </thead>

            <motion.tbody
              key={`fp${currentPage}-fps${pageSize}-${refreshTick}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.14 }}
            >
              {firstLoad ? (
                <SkeletonTheme baseColor="#f1f5f9" highlightColor="#e2e8f0">
                  {Array.from({ length: urlLimit }).map((_, idx) => (
                    <tr key={`sk-${idx}`} style={{ background: "#fff", borderBottom: "1px solid #f1f5f9" }}>
                      {visibleTableColumns.map((column) => (
                        <td key={column.key} style={{ padding: "11px 16px", textAlign: column.key === "applications" || column.key === "available" ? "center" : "left" }}>
                          {column.key === "formType" || column.key === "status" ? (
                            <Skeleton width={72} height={20} borderRadius={14} />
                          ) : column.key === "applications" || column.key === "available" ? (
                            <Skeleton width={36} height={18} borderRadius={9} />
                          ) : (
                            <Skeleton width={Math.max(45, Math.min(column.width - 30, 130))} height={14} />
                          )}
                        </td>
                      ))}
                      <td style={{ padding: "11px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          {canManageForms && <Skeleton circle width={24} height={24} />}
                          {canManageForms && <Skeleton circle width={24} height={24} />}
                          <Skeleton circle width={24} height={24} />
                          <Skeleton circle width={24} height={24} />
                          <Skeleton circle width={24} height={24} />
                          <Skeleton circle width={24} height={24} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </SkeletonTheme>
              ) : error && forms.length === 0 ? (
                <tr>
                  <td colSpan={visibleTableColumns.length + 1} style={{ textAlign: "center", padding: "52px 16px", fontFamily: "Roboto, sans-serif", fontSize: "14px", color: "#b91c1c" }}>
                    {error}
                  </td>
                </tr>
              ) : forms.length === 0 ? (
                <tr>
                  <td colSpan={visibleTableColumns.length + 1} style={{ textAlign: "center", padding: "52px 16px", fontFamily: "Roboto, sans-serif", fontSize: "14px", color: "#94a3b8" }}>
                    No se encontraron formularios.
                  </td>
                </tr>
              ) : (
                forms.map((item, i) => {
                  const tipoCfg = TIPO_CONFIG[item.formType] ?? {
                    bg: "#f1f5f9", color: "#475569", label: item.formType || "—",
                  };
                  const estadoCfg = resolveEstado(item.status);
                  const activo    = estadoCfg === ESTADO_CONFIG.ACTIVA;
                  return (
                    <motion.tr
                      key={item.contextFormId ?? i}
                      className="form-row"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03, duration: 0.18, ease: "easeOut" }}
                      style={{ background: "#fff", borderBottom: "1px solid #f1f5f9" }}
                    >
                      {renderOrderedCells(visibleTableColumns, {
                        code: () => (
                          <td style={{ padding: "11px 16px", fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "13px", color: "#1e293b", fontWeight: 600, whiteSpace: "nowrap" }}>
                            {item.code || "—"}
                          </td>
                        ),
                        formType: () => (
                          <td style={{ padding: "11px 16px", borderLeft: "3px solid transparent", transition: "border-color 0.12s" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: "14px", background: tipoCfg.bg, color: tipoCfg.color, fontSize: "11.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap" }}>
                              {tipoCfg.label}
                            </span>
                          </td>
                        ),
                        recruitmentSource: () => (
                          <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569", whiteSpace: "nowrap" }}>
                            {item.recruitmentSource || "—"}
                          </td>
                        ),
                        formCategory: () => (
                          <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569", whiteSpace: "nowrap" }}>
                            {item.formCategory || "—"}
                          </td>
                        ),
                        client: () => (
                          <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569" }}>
                            {item.client || "—"}
                          </td>
                        ),
                        business: () => (
                          <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569" }}>
                            {item.businessAreaId || "—"}
                          </td>
                        ),
                        recruiter: () => (
                          <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569", whiteSpace: "nowrap" }}>
                            {item.recruiter || "—"}
                          </td>
                        ),
                        vacancy: () => (
                          <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
                            {item.vacancyId || "—"}
                          </td>
                        ),
                        available: () => (
                          <td style={{ padding: "11px 16px", textAlign: "center" }}>
                            {(item.availableApplications ?? 0) > 0 ? (
                              <motion.button
                                onClick={() => handleVerDisponibles(item)}
                                whileHover={{ scale: 1.06, backgroundColor: "rgba(16,185,129,0.16)" }}
                                whileTap={{ scale: 0.94 }}
                                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                title={`Ver ${item.availableApplications} postulación${item.availableApplications === 1 ? "" : "es"} disponible${item.availableApplications === 1 ? "" : "s"}`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "32px",
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  background: "rgba(16,185,129,0.10)",
                                  color: "#047857",
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: "12.5px",
                                  fontWeight: 600,
                                  border: "1px solid rgba(16,185,129,0.22)",
                                  cursor: "pointer",
                                }}
                              >
                                {item.availableApplications}
                              </motion.button>
                            ) : (
                              <span
                                title="Sin postulaciones disponibles"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "32px",
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  background: "#f1f5f9",
                                  color: "#94a3b8",
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: "12.5px",
                                  fontWeight: 600,
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                {item.availableApplications ?? 0}
                              </span>
                            )}
                          </td>
                        ),
                        applications: () => (
                          <td style={{ padding: "11px 16px", textAlign: "center" }}>
                            {(item.totalApplications ?? 0) > 0 ? (
                              <motion.button
                                onClick={() => handleVerPostulaciones(item)}
                                whileHover={{ scale: 1.06, backgroundColor: "rgba(0,101,221,0.14)" }}
                                whileTap={{ scale: 0.94 }}
                                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                title={`Ver ${item.totalApplications} postulación${item.totalApplications === 1 ? "" : "es"}`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "32px",
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  background: "rgba(0,101,221,0.08)",
                                  color: "#0065DD",
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: "12.5px",
                                  fontWeight: 600,
                                  border: "1px solid rgba(0,101,221,0.18)",
                                  cursor: "pointer",
                                }}
                              >
                                {item.totalApplications}
                              </motion.button>
                            ) : (
                              <span
                                title={(item.totalApplications ?? 0) > 0
                                  ? `${item.totalApplications} postulación${item.totalApplications === 1 ? "" : "es"}`
                                  : "Sin postulaciones"}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "32px",
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  background: "#f1f5f9",
                                  color: "#94a3b8",
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: "12.5px",
                                  fontWeight: 600,
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                {item.totalApplications ?? 0}
                              </span>
                            )}
                          </td>
                        ),
                        status: () => (
                          <td style={{ padding: "11px 16px" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: "14px", background: estadoCfg.bg, color: estadoCfg.color, fontSize: "11.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap", border: `1px solid ${estadoCfg.color}22` }}>
                              <span className="form-status-dot" style={{ background: estadoCfg.dot, boxShadow: `0 0 4px ${estadoCfg.dot}88` }} />
                              {estadoCfg.label}
                            </span>
                          </td>
                        ),
                        createdAt: () => (
                          <td style={{ padding: "11px 16px", fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#475569", whiteSpace: "nowrap" }}>
                            {formatDate(item.createdAt)}
                          </td>
                        ),
                      })}
                      <td style={{ padding: "11px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                          <Tooltip text="Ver formulario">
                            <motion.button
                              onClick={() => handleVer(item)}
                              whileHover={{ scale: 1.18 }}
                              whileTap={{ scale: 0.88 }}
                              transition={{ type: "spring", stiffness: 400, damping: 20 }}
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", padding: 0, boxSizing: "border-box", borderRadius: "8px", border: "none", background: "transparent", cursor: "pointer", color: "#0065DD" }}
                            >
                              <VisibilityIcon style={{ fontSize: 18 }} />
                            </motion.button>
                          </Tooltip>

                          <Tooltip text="Ver postulaciones">
                            <motion.button
                              onClick={() => handleVerPostulaciones(item)}
                              whileHover={{ scale: 1.18 }}
                              whileTap={{ scale: 0.88 }}
                              transition={{ type: "spring", stiffness: 400, damping: 20 }}
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", padding: 0, boxSizing: "border-box", borderRadius: "8px", border: "none", background: "transparent", cursor: "pointer", color: "#7c3aed" }}
                            >
                              <PeopleAltIcon style={{ fontSize: 18 }} />
                            </motion.button>
                          </Tooltip>

                          <Tooltip text="Ver enlaces">
                            <motion.button
                              onClick={() => setEnlacesFormulario(item)}
                              whileHover={{ scale: 1.18 }}
                              whileTap={{ scale: 0.88 }}
                              transition={{ type: "spring", stiffness: 400, damping: 20 }}
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", padding: 0, boxSizing: "border-box", borderRadius: "8px", border: "none", background: "transparent", cursor: item.link ? "pointer" : "not-allowed", color: "#0891b2", opacity: item.link ? 1 : 0.4 }}
                            >
                              <QrCode2Icon style={{ fontSize: 18 }} />
                            </motion.button>
                          </Tooltip>

                          <Tooltip text="Estadísticas">
                            <motion.button
                              onClick={() => item.contextFormId && setStatsFormulario(item)}
                              whileHover={{ scale: 1.18 }}
                              whileTap={{ scale: 0.88 }}
                              transition={{ type: "spring", stiffness: 400, damping: 20 }}
                              disabled={!item.contextFormId}
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", padding: 0, boxSizing: "border-box", borderRadius: "8px", border: "none", background: "transparent", cursor: item.contextFormId ? "pointer" : "not-allowed", color: "#0065DD", opacity: item.contextFormId ? 1 : 0.4 }}
                            >
                              <QueryStatsIcon style={{ fontSize: 18 }} />
                            </motion.button>
                          </Tooltip>

                          {/* TODO: Botón métricas oculto temporalmente — ver PENDIENTE_METRICAS.md */}
                          {false && (
                          <Tooltip text="Ver métricas">
                            <motion.button
                              onClick={() => setMetricasFormulario(item)}
                              whileHover={{ scale: 1.18 }}
                              whileTap={{ scale: 0.88 }}
                              transition={{ type: "spring", stiffness: 400, damping: 20 }}
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", padding: 0, boxSizing: "border-box", borderRadius: "8px", border: "none", background: "transparent", cursor: "pointer", color: "#0ea5e9" }}
                            >
                              <InsightsIcon style={{ fontSize: 18 }} />
                            </motion.button>
                          </Tooltip>
                          )}

                          {canManageForms && (
                            <Tooltip text="Editar formulario">
                              <motion.button
                                onClick={() => handleEditar(item)}
                                whileHover={{ scale: 1.18 }}
                                whileTap={{ scale: 0.88 }}
                                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", padding: 0, boxSizing: "border-box", borderRadius: "8px", border: "none", background: "transparent", cursor: "pointer", color: "#475569" }}
                              >
                                <EditIcon style={{ fontSize: 18 }} />
                              </motion.button>
                            </Tooltip>
                          )}

                          {canManageForms && (() => {
                            const isVacInactiva = estadoCfg === ESTADO_CONFIG.VACANTE_INACTIVA;
                            const isBusy        = togglingId === item.contextFormId;
                            const tooltipText   = isVacInactiva
                              ? "La vacante asociada está inactiva"
                              : activo
                                ? "Desactivar formulario"
                                : "Activar formulario";
                            const disabled = isVacInactiva || isBusy;
                            return (
                              <Tooltip text={tooltipText}>
                                <motion.button
                                  onClick={() => !disabled && handleToggleStatus(item)}
                                  whileHover={!disabled ? { scale: 1.18 } : undefined}
                                  whileTap={!disabled ? { scale: 0.88 } : undefined}
                                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                  disabled={disabled}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    boxSizing: "border-box",
                                    borderRadius: "8px",
                                    border: "none",
                                    background: "transparent",
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    color: activo ? "#10b981" : "#94a3b8",
                                    opacity: isBusy ? 0.55 : 1,
                                  }}
                                >
                                  {isBusy ? (
                                    <span
                                      style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        border: "2px solid #cbd5e1",
                                        borderTopColor: "#0065DD",
                                        animation: "spin 0.7s linear infinite",
                                        display: "inline-block",
                                      }}
                                    />
                                  ) : (
                                    <ToggleOnIcon style={{ fontSize: 22, transform: activo ? "none" : "scaleX(-1)" }} />
                                  )}
                                </motion.button>
                              </Tooltip>
                            );
                          })()}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </motion.tbody>
          </table>
        </div>
      </div>

      {/* ── Pagination ──────────────────────────────────────────────────────── */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* ── Modal crear formulario ─────────────────────────────────────────── */}
      <AnimatePresence>
        {showCrearModal && (
          <CrearFormularioModal
            onClose={() => setShowCrearModal(false)}
            onCreated={() => setRefreshTick((t) => t + 1)}
          />
        )}
      </AnimatePresence>

      {/* ── Modal ver enlaces ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {enlacesFormulario && (
          <VerEnlacesModal
            formulario={enlacesFormulario}
            onClose={() => setEnlacesFormulario(null)}
          />
        )}
      </AnimatePresence>

      {/* ── Modal editar formulario ────────────────────────────────────────── */}
      <AnimatePresence>
        {editarFormulario && (
          <EditarFormularioModal
            formulario={editarFormulario}
            onClose={() => setEditarFormulario(null)}
            onSaved={() => setRefreshTick((t) => t + 1)}
          />
        )}
      </AnimatePresence>

      {/* ── Modal ver formulario ───────────────────────────────────────────── */}
      <AnimatePresence>
        {verFormulario && (
          <VerFormularioModal
            formulario={verFormulario}
            onClose={() => setVerFormulario(null)}
          />
        )}
      </AnimatePresence>

      {/* ── Modal métricas formulario ──────────────────────────────────────── */}
      <AnimatePresence>
        {metricasFormulario && (
          <MetricasFormularioModal
            formulario={metricasFormulario}
            onClose={() => setMetricasFormulario(null)}
          />
        )}
      </AnimatePresence>

      {/* ── Modal estadísticas generales ───────────────────────────────────── */}
      <AnimatePresence>
        {showGeneralStats && (
          <EstadisticasModal onClose={() => setShowGeneralStats(false)} />
        )}
      </AnimatePresence>

      {/* ── Modal estadísticas por formulario ──────────────────────────────── */}
      <AnimatePresence>
        {statsFormulario && (
          <EstadisticasModal
            contextForm={{ contextFormId: statsFormulario.contextFormId, code: statsFormulario.code }}
            onClose={() => setStatsFormulario(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default TablaFormularios;
